/**
 * Client-side SQL linting — catch typos *before* save.
 *
 * The backend ``validate_sql()`` is still the source of truth (it checks
 * dangerous keywords and runs the query) but these helpers surface
 * instant feedback while the user types:
 *
 *   - ``findReferencedColumns(sql)`` pulls ``"Quoted"`` identifiers out.
 *   - ``lintColumnReferences(sql, availableColumns)`` returns any column
 *     references not present in the DuckDB table.
 *
 * We treat **unquoted bare identifiers** as SQL keywords / functions
 * and ignore them — ``SUM``, ``CASE``, ``WHEN``, ``THEN``, ``ELSE``,
 * ``END``, ``AVG``, ``COUNT``, ``DISTINCT``. Quoted identifiers are the
 * unambiguous signal that a column is being referenced.
 */

const COLUMN_REF_RE = /"((?:[^"]|"")+)"/g;

export function findReferencedColumns(sql: string): string[] {
  if (!sql) return [];
  const out = new Set<string>();
  let match: RegExpExecArray | null;
  const re = new RegExp(COLUMN_REF_RE.source, "g");
  while ((match = re.exec(sql)) !== null) {
    const raw = match[1] ?? "";
    // DuckDB doubles embedded quotes: "Field ""Name""" → Field "Name"
    out.add(raw.replace(/""/g, '"'));
  }
  return Array.from(out);
}

export interface SqlLintResult {
  /** Column references present in the SQL that don't exist in the table. */
  unknownColumns: string[];
  /** Column references whose case does not match any known column. */
  caseMismatches: Array<{ used: string; suggest: string }>;
}

export function lintColumnReferences(
  sql: string,
  availableColumns: ReadonlyArray<string>,
): SqlLintResult {
  const refs = findReferencedColumns(sql);
  if (refs.length === 0 || availableColumns.length === 0) {
    return { unknownColumns: [], caseMismatches: [] };
  }
  const known = new Set(availableColumns);
  const knownLower = new Map(
    availableColumns.map((c) => [c.toLowerCase(), c]),
  );
  const unknown: string[] = [];
  const caseMismatches: Array<{ used: string; suggest: string }> = [];
  for (const ref of refs) {
    if (known.has(ref)) continue;
    const caseFix = knownLower.get(ref.toLowerCase());
    if (caseFix) {
      caseMismatches.push({ used: ref, suggest: caseFix });
    } else {
      unknown.push(ref);
    }
  }
  return { unknownColumns: unknown, caseMismatches };
}
